/**
 * Routes
 * @module routes
 */

import React, { Suspense, lazy } from "react";
import { Route, Redirect, Switch } from "./interfaces/router";
import { ROUTES } from "./constants/routes";

// Lazy-loaded Route Components for authenticated app
// High priority: Core app routes (dashboard, credentials)
const Dashboard = lazy(() =>
  import(
    /* webpackChunkName: "dashboard", webpackPrefetch: true */ "./containers/dashboard/dashboard.container"
  ),
);
const CredentialsContainer = lazy(() =>
  import(
    /* webpackChunkName: "credentials", webpackPrefetch: true */ "./containers/credentials/credentials.container"
  ),
);

// Medium priority: User-specific routes
const PersonalDocuments = lazy(() =>
  import(
    /* webpackChunkName: "personal-documents", webpackPrefetch: true */ "./containers/personal-documents/personal-documents.container"
  ),
);
const Profile = lazy(() =>
  import(
    /* webpackChunkName: "profile" */ "./components/user/services/profile/profile"
  ),
);
const NotificationPreferences = lazy(() =>
  import(
    /* webpackChunkName: "notification-preferences" */ "./components/user/notification-preferences"
  ),
);
const InvestmentPortfolio = lazy(() =>
  import(
    /* webpackChunkName: "investment-portfolio" */ "./components/user/myPortfolio"
  ),
);

// Lower priority: Advanced features
const DealflowRoute = lazy(() =>
  import(
    /* webpackChunkName: "dealflow" */ "./containers/dealflow/DealflowRoute"
  ),
);
const CompanyRoutesContainer = lazy(() =>
  import(
    /* webpackChunkName: "company-routes" */ "./containers/company/company-routes.container"
  ),
);
const Onboarding = lazy(() =>
  import(/* webpackChunkName: "onboarding" */ "./containers/onboarding"),
);

// Keep signout synchronous as it's a simple action
import Signout from "./containers/credentials/signout.container";
import LoadingPanel from "./components/loadingPanel";

const Routes = () => {
  return (
    <Suspense fallback={<LoadingPanel size={32} />}>
      <Switch>
        <Route exact path={ROUTES.SIGNIN} component={CredentialsContainer} />
        <Route exact path={ROUTES.SIGNUP} component={CredentialsContainer} />
        <Route
          exact
          path={ROUTES.FORGOT_PASSWORD}
          component={CredentialsContainer}
        />
        <Route exact path={ROUTES.SIGNOUT} component={Signout} />

        <Route exact path={ROUTES.ONBOARDING} component={Onboarding} />

        <Redirect exact from="/" to="/dashboard" />
        <Route exact path={ROUTES.DASHBOARD} component={Dashboard} />
        <Route
          exact
          path={ROUTES.PERSONAL_DOCUMENTS}
          component={PersonalDocuments}
        />

        <Route exact path={ROUTES.PROFILE} component={Profile} />
        <Route
          exact
          path={ROUTES.NOTIFICATION_PREFERENCES}
          component={NotificationPreferences}
        />
        <Route
          path={ROUTES.PORTFOLIO}
          render={(props) => (
            <InvestmentPortfolio {...props} ownerType="user" />
          )}
        />
        <Route path={ROUTES.DEALFLOW} component={DealflowRoute} />

        <Route path="/:company" component={CompanyRoutesContainer} />
      </Switch>
    </Suspense>
  );
};

export default Routes;
